import { CheckboxGroup, Label } from 'react-aria-components';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { navigate } from 'wouter/use-browser-location';
import Alert from '~/components/Alert';
import TextAreaField from '~/components/forms/TextAreaField';
import { FormAction, FormBody } from '~/components/FormUtils';
import PageHeader from '~/components/PageHeader';
import Button from '~/components/ui/Button';
import Checkbox from '~/components/ui/Checkbox';
import Stack from '~/components/ui/Stack';
import useCancelSubscription from '~/modules/subscription/useCancelSubscription';

type FormValues = {
  reason1: string[];
  reason2: string;
};

function Cancel() {
  const { t } = useTranslation('', { keyPrefix: 'subscription.cancel' });
  const { mutateAsync: cancelSubscription, isError } = useCancelSubscription();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<FormValues>({
    mode: 'onTouched',
    values: {
      reason1: [],
      reason2: '',
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await cancelSubscription([...data.reason1, data.reason2].join(';'));
    navigate('/subscription');
  });

  return (
    <Stack col gap={6} className="flex-1">
      <PageHeader id="cancelSubscription" />
      {isError && <Alert variant="error" description={t('error')} />}
      <form onSubmit={onSubmit}>
        <FormBody className="gap-8">
          <Controller
            name="reason1"
            control={control}
            render={({ field }) => (
              <CheckboxGroup
                className="flex flex-col gap-4"
                value={field.value}
                onChange={field.onChange}
              >
                <Label className="text-sm font-medium text-input-label">{t('reasonLabel')}</Label>
                <Checkbox value={t('reasons.noNeed')} label={t('reasons.noNeed')} />
                <Checkbox value={t('reasons.tooExpensive')} label={t('reasons.tooExpensive')} />
                <Checkbox value={t('reasons.dontUse')} label={t('reasons.dontUse')} />
                <Checkbox
                  value={t('reasons.technicalIssues')}
                  label={t('reasons.technicalIssues')}
                />
              </CheckboxGroup>
            )}
          />

          <TextAreaField
            control={control}
            name="reason2"
            label={t('otherThoughts')}
            className="w-full max-w-[400px]"
            classNameInput="resize-none"
          />
        </FormBody>
        <FormAction>
          <Button variant="outlined" to="/subscription" className="w-full sm:min-w-[200px]">
            {t('return')}
          </Button>
          <Button type="submit" isPending={isSubmitting} isDisabled={!isValid}>
            {t('submit')}
          </Button>
        </FormAction>
      </form>
    </Stack>
  );
}

export default Cancel;
